import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  iconArrowDownStepCourses,
  iconBookYellowStep2Courses,
  iconCongratulationStep2Courses,
  iconDeleteStep2Courses,
  iconEditTableCourses,
  iconFileStep2Courses,
  iconLightBulbStepCourses,
  iconPlusStep2Courses,
  iconQuizStep2Courses,
  iconTranslateStepCourses,
  iconVideoStep2Courses,
} from '../../../assets';
import type { Theme } from '../../../theme';
import { LanguageTabs, type LangKey } from './LanguageTabs';
import { LessonItem, type LessonType } from './LessonItem';

/** Erros de validação por campo (chaves i18n em courses.create.*). */
export interface ModuleEditorValidationErrors {
  moduleName?: string;
  lessons?: string;
}

export type { LangKey, LessonType };

export interface ModuleEditorLesson {
  id: string;
  type: LessonType;
  xp: number;
  /** Nome da lição em EN/ES (ex.: preenchido no modal de vídeo). */
  name?: Record<LangKey, string>;
}

export interface ModuleEditorProps {
  theme: Theme;
  /** ID do módulo (para drag-and-drop de reorder). */
  moduleId: string;
  /** Valores do módulo */
  moduleName: Record<LangKey, string>;
  currentLang: LangKey;
  onLangChange: (lang: LangKey) => void;
  onModuleNameChange: (lang: LangKey, value: string) => void;
  lessons: ModuleEditorLesson[];
  /** Módulo está sendo arrastado (feedback visual). */
  isModuleDragging?: boolean;
  /** Módulo é o alvo do drop (fica mais apagado durante o arraste). */
  isModuleDropTarget?: boolean;
  /** Handlers de drag-and-drop para reorder de módulos. */
  onModuleDragStart?: (e: React.DragEvent, moduleId: string) => void;
  onModuleDragOver?: (e: React.DragEvent, targetModuleId: string) => void;
  onModuleDragLeave?: () => void;
  onModuleDrop?: (e: React.DragEvent, targetModuleId: string) => void;
  onModuleDragEnd?: () => void;
  /** Reorder de lições dentro do módulo: (fromIndex, toIndex). */
  onLessonMove?: (fromIndex: number, toIndex: number) => void;
  /** Labels i18n */
  labels: {
    moduleName: string;
    moduleNamePlaceholder: string;
    multilingualHint: string;
    lessonsCount: (count: number) => string;
    addLesson: string;
    addLessonAria: string;
    deleteModuleAria: string;
    deleteLessonAria: string;
    editLessonAria: string;
    lessonXpAria: string;
    dragHandle: string;
    expandModuleAria: string;
    collapseModuleAria: string;
    moduleNameLanguageAria: string;
    lessonTypeVideo: string;
    lessonTypeFile: string;
    lessonTypeQuiz: string;
    lesson: string;
  };
  /** Callbacks */
  /** Chamado ao escolher um tipo no dropdown Add Lesson */
  onAddLesson: (type: LessonType) => void;
  /** Se definido, ao clicar em Video abre o modal de configuração em vez de adicionar a lição direto */
  onAddVideoLessonRequest?: () => void;
  /** Se definido, ao clicar em File abre o modal de configuração em vez de adicionar a lição direto */
  onAddFileLessonRequest?: () => void;
  /** Se definido, ao clicar em Quiz abre o modal de configuração em vez de adicionar a lição direto */
  onAddQuizLessonRequest?: () => void;
  onDeleteModule: () => void;
  onDeleteLesson: (lessonId: string) => void;
  /** Se definido, ao clicar em Editar na lição abre o modal correspondente (video/file/quiz) para editar */
  onEditLesson?: (lesson: ModuleEditorLesson) => void;
  onLessonXpChange: (lessonId: string, xp: number) => void;
  /** Expandido por padrão */
  defaultExpanded?: boolean;
  /** Erros de validação (chaves i18n) – exibidos abaixo do Module Name e da área de lições */
  validationErrors?: ModuleEditorValidationErrors;
}

/** Ícone de alça de arrastar: grid 3x3 de pontos */
function DragHandleIcon({ theme }: { theme: Theme }) {
  return (
    <svg
      width={20}
      height={20}
      viewBox="0 0 20 20"
      fill={theme.palette.text.secondary}
      aria-hidden
      style={{ flexShrink: 0 }}
    >
      {[0, 1, 2].map(row =>
        [0, 1, 2].map(col => (
          <circle
            key={`${row}-${col}`}
            cx={4 + col * 6}
            cy={4 + row * 6}
            r={1.5}
          />
        ))
      )}
    </svg>
  );
}

const lessonTypeIcons: Record<LessonType, string> = {
  video: iconVideoStep2Courses,
  file: iconFileStep2Courses,
  quiz: iconQuizStep2Courses,
};

/** Mapeamento de layout a partir do theme (designTokens) */
function getModuleEditorLayout(theme: Theme) {
  const sc = theme.palette.createCourse;
  const s = theme.spacing;
  const sh = theme.shape;
  const ty = theme.typography;
  return {
    cardPadding: s.lg,
    cardRadius: sh.borderRadius.sm3,
    cardBorderWidth: 1,
    headerGapLeft: s.sm2,
    headerGapRight: s.md,
    headerMarginBottom: s.lg,
    dragSize: sc.dragHandleSize,
    bookIconSize: sc.iconSize,
    lessonsCountFontSize: ty.fontSize.sm,
    addLessonPaddingY: theme.spacing.buttonSmallY,
    addLessonPaddingX: theme.spacing.buttonSmallX,
    addLessonGap: s.xs,
    addLessonFontSize: ty.fontSize.xs,
    addLessonRadius: sh.borderRadius.sm,
    addLessonIconSize: sc.iconSizeSmall,
    chevronSize: sc.chevronSize,
    deleteBtnSize: sc.deleteBtnSize,
    deleteBtnRadius: sh.borderRadius.sm,
    deleteIconSize: sc.dragHandleSize,
    detailsMarginBottom: s.lg,
    labelRowGap: s.xs,
    labelRowMarginBottom: s.sm,
    labelFontSize: ty.fontSize.sm,
    translateIconSize: sc.addModuleIconSize,
    tabsMarginBottom: s.sm,
    inputPaddingY: theme.spacing.inputY,
    inputPaddingX: theme.spacing.inputX,
    inputFontSize: ty.fontSize.base,
    inputRadius: sh.borderRadius.sm2,
    hintMarginTop: s.sm,
    hintGap: s.xs,
    hintFontSize: ty.fontSize.xs,
    lightBulbSize: sc.addModuleIconSize,
    lessonsListMarginTop: s.lg,
    lessonsListGap: s.md,
    selectPaddingY: s.xs,
    selectPaddingX: theme.spacing.inputX,
    selectFontSize: ty.fontSize.xs,
    selectRadius: sh.borderRadius.sm,
  };
}

/**
 * Card do editor de módulo: barra superior (drag, livro, X Lessons, + Add Lesson com chevron, lixeira),
 * seção Module Name com LanguageTabs, input e hint, lista de LessonItem.
 */
export function ModuleEditor({
  theme,
  moduleId,
  moduleName,
  currentLang,
  onLangChange,
  onModuleNameChange,
  lessons,
  isModuleDragging = false,
  isModuleDropTarget = false,
  onModuleDragStart,
  onModuleDragOver,
  onModuleDragLeave,
  onModuleDrop,
  onModuleDragEnd,
  onLessonMove,
  labels,
  onAddLesson,
  onAddVideoLessonRequest,
  onAddFileLessonRequest,
  onAddQuizLessonRequest,
  onDeleteModule,
  onDeleteLesson,
  onEditLesson,
  onLessonXpChange,
  defaultExpanded = true,
  validationErrors,
}: ModuleEditorProps) {
  const { t } = useTranslation();
  const LAYOUT = getModuleEditorLayout(theme);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [addLessonMenuOpen, setAddLessonMenuOpen] = useState(false);
  const addLessonMenuRef = useRef<HTMLDivElement>(null);
  /** Índice da lição sendo arrastada (reorder dentro do módulo). */
  const [draggedLessonIndex, setDraggedLessonIndex] = useState<number | null>(null);
  /** Índice da lição sobre a qual o cursor está (alvo do drop – fica mais apagada). */
  const [dragOverLessonIndex, setDragOverLessonIndex] = useState<number | null>(null);

  const toggleExpanded = () => setExpanded(prev => !prev);

  const handleLessonDragStart = (e: React.DragEvent, lessonIndex: number) => {
    e.dataTransfer.setData('application/x-lesson-index', String(lessonIndex));
    e.dataTransfer.effectAllowed = 'move';
    setDraggedLessonIndex(lessonIndex);
    setDragOverLessonIndex(null);
  };

  const handleLessonDragEnd = () => {
    setDraggedLessonIndex(null);
    setDragOverLessonIndex(null);
  };

  const handleLessonDragOver = (e: React.DragEvent, targetIndex: number) => {
    if (!e.dataTransfer.types.includes('application/x-lesson-index')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverLessonIndex(targetIndex);
  };

  const handleLessonDragLeave = () => {
    setDragOverLessonIndex(null);
  };

  const handleLessonDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    setDragOverLessonIndex(null);
    if (!e.dataTransfer.types.includes('application/x-lesson-index')) return;
    const sourceStr = e.dataTransfer.getData('application/x-lesson-index');
    if (sourceStr === '') return;
    const sourceIndex = parseInt(sourceStr, 10);
    if (Number.isNaN(sourceIndex) || sourceIndex === targetIndex) return;
    onLessonMove?.(sourceIndex, targetIndex);
    setDraggedLessonIndex(null);
  };

  useEffect(() => {
    if (!addLessonMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        addLessonMenuRef.current &&
        !addLessonMenuRef.current.contains(e.target as Node)
      ) {
        setAddLessonMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [addLessonMenuOpen]);

  const cardStyle: React.CSSProperties = {
    backgroundColor: theme.palette.background.paper,
    borderRadius: LAYOUT.cardRadius,
    border: `${LAYOUT.cardBorderWidth}px solid ${theme.palette.primary.main}`,
    padding: LAYOUT.cardPadding,
    boxShadow: 'none',
    opacity: isModuleDragging ? 0.6 : isModuleDropTarget ? 0.5 : 1,
    transition: 'opacity 0.15s ease',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: LAYOUT.headerGapRight,
    marginBottom: expanded ? LAYOUT.headerMarginBottom : 0,
    flexWrap: 'wrap',
  };

  const moduleNameTitleStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: LAYOUT.labelFontSize,
    fontWeight: theme.typography.fontWeight.semibold,
    lineHeight: 1.4,
    color: theme.palette.text.primary,
    flex: 1,
    minWidth: 0,
    textAlign: 'left',
  };

  const headerLeftStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.headerGapLeft,
  };

  const dragStyle: React.CSSProperties = {
    width: LAYOUT.dragSize,
    height: LAYOUT.dragSize,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'grab',
    flexShrink: 0,
  };

  const headerRightStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.headerGapRight,
    flexShrink: 0,
  };

  const lessonsCountStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: LAYOUT.lessonsCountFontSize,
    fontWeight: theme.typography.fontWeight.medium,
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  };

  const addLessonButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.addLessonGap,
    padding: `${LAYOUT.addLessonPaddingY} ${LAYOUT.addLessonPaddingX}`,
    fontFamily: theme.typography.fontFamily,
    fontSize: LAYOUT.addLessonFontSize,
    fontWeight: theme.typography.fontWeight.semibold,
    lineHeight: 1.25,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.main}`,
    borderRadius: LAYOUT.addLessonRadius,
    cursor: 'pointer',
  };

  const deleteModuleButtonStyle: React.CSSProperties = {
    width: LAYOUT.deleteBtnSize,
    height: LAYOUT.deleteBtnSize,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    borderRadius: LAYOUT.deleteBtnRadius,
    backgroundColor: theme.palette.background.paper,
    cursor: 'pointer',
    flexShrink: 0,
  };

  const detailsSectionStyle: React.CSSProperties = {
    marginBottom: LAYOUT.detailsMarginBottom,
  };

  const labelRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.labelRowGap,
    marginBottom: LAYOUT.labelRowMarginBottom,
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: LAYOUT.labelFontSize,
    fontWeight: theme.typography.fontWeight.semibold,
    lineHeight: 1.4,
    color: theme.palette.text.primary,
  };

  const requiredStyle: React.CSSProperties = {
    color: theme.palette.error.main,
  };

  const translateIconStyle: React.CSSProperties = {
    width: LAYOUT.translateIconSize,
    height: LAYOUT.translateIconSize,
    objectFit: 'contain',
  };

  const tabsWrapStyle: React.CSSProperties = {
    marginBottom: LAYOUT.tabsMarginBottom,
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: `${LAYOUT.inputPaddingY} ${LAYOUT.inputPaddingX}`,
    fontFamily: theme.typography.fontFamily,
    fontSize: LAYOUT.inputFontSize,
    lineHeight: 1.4,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.input.background,
    border: `1px solid ${theme.palette.border.input}`,
    borderRadius: LAYOUT.inputRadius,
    boxSizing: 'border-box',
    outline: 'none',
  };

  const hintRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.hintGap,
    marginTop: LAYOUT.hintMarginTop,
  };

  const hintStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: LAYOUT.hintFontSize,
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  };

  const errorStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: LAYOUT.hintFontSize,
    lineHeight: 1.4,
    color: theme.palette.error.main,
    marginTop: LAYOUT.hintGap,
    marginBottom: 0,
  };

  const lightBulbIconStyle: React.CSSProperties = {
    width: LAYOUT.lightBulbSize,
    height: LAYOUT.lightBulbSize,
    objectFit: 'contain',
    flexShrink: 0,
  };

  const lessonsListStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: LAYOUT.lessonsListGap,
    marginTop: LAYOUT.lessonsListMarginTop,
  };

  const typeLabels: Record<LessonType, string> = {
    video: labels.lessonTypeVideo,
    file: labels.lessonTypeFile,
    quiz: labels.lessonTypeQuiz,
  };

  const displayName =
    moduleName[currentLang]?.trim() || labels.moduleNamePlaceholder;

  const handleSectionDragOver = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes('application/x-module-id')) return;
    onModuleDragOver?.(e, moduleId);
  };

  const handleSectionDrop = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes('application/x-module-id')) return;
    onModuleDrop?.(e, moduleId);
  };

  return (
    <section
      style={cardStyle}
      aria-labelledby={`module-editor-heading-${moduleId}`}
      onDragOver={handleSectionDragOver}
      onDragLeave={onModuleDragLeave}
      onDrop={handleSectionDrop}
    >
      <header style={headerStyle}>
        <div style={{ ...headerLeftStyle, flex: 1, minWidth: 0 }}>
          <div
            role="button"
            tabIndex={0}
            style={{
              ...dragStyle,
              cursor: onModuleDragStart ? 'grab' : 'default',
            }}
            aria-label={labels.dragHandle}
            title={labels.dragHandle}
            draggable={Boolean(onModuleDragStart)}
            onDragStart={
              onModuleDragStart
                ? (e: React.DragEvent) => {
                    onModuleDragStart(e, moduleId);
                  }
                : undefined
            }
            onDragEnd={onModuleDragEnd}
          >
            <DragHandleIcon theme={theme} />
          </div>
          <button
            type="button"
            style={{
              flex: 1,
              minWidth: 0,
              cursor: 'pointer',
              border: 'none',
              background: 'none',
              padding: 0,
              margin: 0,
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              gap: LAYOUT.headerGapLeft,
            }}
            onClick={toggleExpanded}
            aria-expanded={expanded}
            aria-label={expanded ? labels.collapseModuleAria : labels.expandModuleAria}
          >
            <img
              src={iconBookYellowStep2Courses}
              alt=""
              width={LAYOUT.bookIconSize}
              height={LAYOUT.bookIconSize}
              style={{ objectFit: 'contain', flexShrink: 0 }}
              aria-hidden
            />
            <span style={moduleNameTitleStyle} id={`module-editor-heading-${moduleId}`}>
              {displayName}
            </span>
          </button>
        </div>
        <div style={headerRightStyle} onClick={e => e.stopPropagation()}>
          <span style={lessonsCountStyle}>
            {labels.lessonsCount(lessons.length)}
          </span>
          <div
            ref={addLessonMenuRef}
            style={{ position: 'relative', flexShrink: 0 }}
          >
            <button
              type="button"
              style={addLessonButtonStyle}
              onClick={() => setAddLessonMenuOpen(prev => !prev)}
              aria-label={labels.addLessonAria}
              aria-expanded={addLessonMenuOpen}
              aria-haspopup="menu"
            >
              <img
                src={iconPlusStep2Courses}
                alt=""
                width={LAYOUT.addLessonIconSize}
                height={LAYOUT.addLessonIconSize}
                style={{ objectFit: 'contain', flexShrink: 0 }}
                aria-hidden
              />
              {labels.addLesson}
              <img
                src={iconArrowDownStepCourses}
                alt=""
                width={LAYOUT.chevronSize}
                height={LAYOUT.chevronSize}
                style={{
                  objectFit: 'contain',
                  flexShrink: 0,
                  marginLeft: 2,
                  transform: addLessonMenuOpen ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s ease',
                }}
                aria-hidden
              />
            </button>
            {addLessonMenuOpen && (
              <div
                role="menu"
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  marginTop: 4,
                  minWidth: 140,
                  padding: 4,
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.border.main}`,
                  borderRadius: LAYOUT.addLessonRadius,
                  boxShadow: theme.palette.createCourse.dropdownBoxShadow,
                  zIndex: 10,
                }}
              >
                {(['video', 'file', 'quiz'] as const).map(type => (
                  <button
                    key={type}
                    type="button"
                    role="menuitem"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      width: '100%',
                      padding: '8px 12px',
                      fontFamily: theme.typography.fontFamily,
                      fontSize: LAYOUT.addLessonFontSize,
                      fontWeight: theme.typography.fontWeight.medium,
                      color: theme.palette.text.primary,
                      backgroundColor: 'transparent',
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                    onClick={() => {
                      if (
                        type === 'video' &&
                        typeof onAddVideoLessonRequest === 'function'
                      ) {
                        onAddVideoLessonRequest();
                      } else if (
                        type === 'file' &&
                        typeof onAddFileLessonRequest === 'function'
                      ) {
                        onAddFileLessonRequest();
                      } else if (
                        type === 'quiz' &&
                        typeof onAddQuizLessonRequest === 'function'
                      ) {
                        onAddQuizLessonRequest();
                      } else {
                        onAddLesson(type);
                      }
                      setAddLessonMenuOpen(false);
                    }}
                  >
                    <img
                      src={lessonTypeIcons[type]}
                      alt=""
                      width={LAYOUT.addLessonIconSize}
                      height={LAYOUT.addLessonIconSize}
                      style={{ objectFit: 'contain', flexShrink: 0 }}
                      aria-hidden
                    />
                    {typeLabels[type]}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            style={{
              width: LAYOUT.deleteBtnSize,
              height: LAYOUT.deleteBtnSize,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              borderRadius: LAYOUT.deleteBtnRadius,
              backgroundColor: 'transparent',
              cursor: 'pointer',
              flexShrink: 0,
              color: theme.palette.text.secondary,
            }}
            onClick={toggleExpanded}
            aria-expanded={expanded}
            aria-label={expanded ? labels.collapseModuleAria : labels.expandModuleAria}
            title={expanded ? labels.collapseModuleAria : labels.expandModuleAria}
          >
            <img
              src={iconArrowDownStepCourses}
              alt=""
              width={LAYOUT.chevronSize}
              height={LAYOUT.chevronSize}
              style={{
                objectFit: 'contain',
                flexShrink: 0,
                transform: expanded ? 'none' : 'rotate(-90deg)',
                transition: 'transform 0.2s ease',
              }}
              aria-hidden
            />
          </button>
          <button
            type="button"
            style={deleteModuleButtonStyle}
            onClick={onDeleteModule}
            aria-label={labels.deleteModuleAria}
            title={labels.deleteModuleAria}
          >
            <img
              src={iconDeleteStep2Courses}
              alt=""
              width={LAYOUT.deleteIconSize}
              height={LAYOUT.deleteIconSize}
              style={{ objectFit: 'contain' }}
              aria-hidden
            />
          </button>
        </div>
      </header>

      {expanded && (
        <>
          <div style={detailsSectionStyle}>
            <div style={labelRowStyle}>
              <span style={labelStyle}>{labels.moduleName}</span>
              <span style={requiredStyle}>*</span>
              <img
                src={iconTranslateStepCourses}
                alt=""
                style={translateIconStyle}
                aria-hidden
              />
            </div>
            <div style={tabsWrapStyle}>
              <LanguageTabs
                theme={theme}
                value={currentLang}
                onChange={onLangChange}
                options={[
                  { value: 'en', label: 'EN' },
                  { value: 'es', label: 'ES' },
                ]}
                aria-label={labels.moduleNameLanguageAria}
              />
            </div>
            <input
              type="text"
              value={moduleName[currentLang]}
              onChange={e => onModuleNameChange(currentLang, e.target.value)}
              placeholder={labels.moduleNamePlaceholder}
              style={{
                ...inputStyle,
                ...(validationErrors?.moduleName
                  ? { border: `1px solid ${theme.palette.error.main}` }
                  : {}),
              }}
              aria-required
              aria-invalid={Boolean(validationErrors?.moduleName)}
            />
            {validationErrors?.moduleName && (
              <p style={errorStyle} role="alert">
                {t(`courses.create.${validationErrors.moduleName}`)}
              </p>
            )}
            <div style={hintRowStyle}>
              <img
                src={iconLightBulbStepCourses}
                alt=""
                style={lightBulbIconStyle}
                aria-hidden
              />
              <span style={hintStyle}>{labels.multilingualHint}</span>
            </div>
          </div>

          {validationErrors?.lessons && (
            <p style={{ ...errorStyle, marginTop: LAYOUT.lessonsListMarginTop }} role="alert">
              {t(`courses.create.${validationErrors.lessons}`)}
            </p>
          )}

          {lessons.length > 0 && (
            <div style={lessonsListStyle}>
              {lessons.map((lesson, index) => (
                <LessonItem
                  key={lesson.id}
                  theme={theme}
                  type={lesson.type}
                  typeLabel={typeLabels[lesson.type]}
                  title={
                    lesson.name?.en?.trim() || lesson.name?.es?.trim()
                      ? (lesson.name[currentLang]?.trim() ||
                          lesson.name.en?.trim() ||
                          lesson.name.es?.trim() ||
                          '')
                      : `${typeLabels[lesson.type]} ${labels.lesson} ${index + 1}`
                  }
                  xp={lesson.xp}
                  onXpChange={xp => onLessonXpChange(lesson.id, xp)}
                  onDelete={() => onDeleteLesson(lesson.id)}
                  deleteAriaLabel={labels.deleteLessonAria}
                  xpAriaLabel={labels.lessonXpAria}
                  lessonTypeIconSrc={lessonTypeIcons[lesson.type]}
                  congratulationIconSrc={iconCongratulationStep2Courses}
                  deleteIconSrc={iconDeleteStep2Courses}
                  onEdit={
                    onEditLesson
                      ? () => onEditLesson(lesson as ModuleEditorLesson)
                      : undefined
                  }
                  editAriaLabel={labels.editLessonAria}
                  editIconSrc={iconEditTableCourses}
                  lessonIndex={index}
                  isLessonDragging={draggedLessonIndex === index}
                  isLessonDropTarget={dragOverLessonIndex === index}
                  onLessonDragStart={onLessonMove ? handleLessonDragStart : undefined}
                  onLessonDragOver={onLessonMove ? handleLessonDragOver : undefined}
                  onLessonDragLeave={onLessonMove ? handleLessonDragLeave : undefined}
                  onLessonDrop={onLessonMove ? handleLessonDrop : undefined}
                  onLessonDragEnd={handleLessonDragEnd}
                  dragHandleAriaLabel={labels.dragHandle}
                />
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}
